/**
 * @fileoverview 動画詳細ページの1カラムレイアウト（動画ブロック＋コンテンツカード）
 * @description 特定IDパターン(contact済み)・通常パターン共通で利用する1カラムの見た目。
 *              フォーム/モーダルは保持せず、ロック中サムネイルのクリックは click:thumbnail で親へ通知する。
 */
<script lang="ts">
import { defineComponent, PropType, computed } from 'vue'
import SolutionContentsIntro from '@/components/solutions/SolutionContentsIntro.vue'
import { formatToHms } from '@/utilities/formatter'

export default defineComponent({
  name: 'VideoSingleColumnLayout',

  components: {
    SolutionContentsIntro,
  },

  props: {
    video: {
      type: Object as PropType<SolutionContentItem>,
      required: true,
    },

    corporateBrandColor: {
      type: Object as PropType<{
        primary: string,
        secondary: string,
      }>,
      default: () => ({ primary: '', secondary: '' }),
    },
  },

  emits: [
    'click:thumbnail',
  ],

  setup(props) {
    /**
     * ビデオ再生時間
     * @description Wistia で受け取った「秒」単位の時間の数値を「HH:mm:ss」形式に変換して表示
     */
    const videoDuration = computed(() => {
      return props.video.duration
        ? formatToHms(props.video.duration)
        : undefined
    })

    return {
      videoDuration,
    }
  },
})
</script>

<template>
  <SolutionContentsIntro
    :item="video"
    :brand-color="corporateBrandColor"
    has-heading>
    <template #thumbImage>
      <div
        class="l-unit__videoPlayer"
        :data-thumbnail="video.isShowBlock">
        <div
          v-if="video.isShowBlock"
          class="l-unit__videoPlayer--image"
          @click.prevent="$emit('click:thumbnail')">
          <img
            v-if="video.thumbImageUrl"
            class="img"
            :src="video.thumbImageUrl"
            fetchpriority="high"
            alt=""
            data-is-liquid="true">

          <div
            v-else
            class="o-label__noThumbnail">
            <p class="o-label__noThumbnail--item">
              <i class="i-camera" />
              No Image
            </p>
          <!-- /.o-label__noThumbnail --></div>
        <!-- /.l-unit__videoPlayer image --></div>

        <div
          v-else
          class="l-unit__videoPlayer--image">
          <!-- YouTube動画の場合 -->
          <div
            v-if="video.isYoutube"
            class="o-figure__youtube">
            <iframe
              class="o-figure__youtube--item"
              :src="`https://www.youtube.com/embed/${video.mediaId}`"
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowfullscreen />
          <!-- /.o-figure__youtube --></div>

          <!-- Wistia動画の場合 -->
          <div
            v-else
            class="wistia_responsive_padding"
            style="padding: 56.25% 0 0 0; position: relative;">
            <div
              class="wistia_responsive_wrapper"
              style="height: 100%; left: 0; position: absolute; top: 0; width: 100%;">
              <div
                :class="`wistia_embed wistia_async_${video.mediaId} seo=false videoFoam=true`"
                style="height: 100%; width: 100%">
                &nbsp;
              </div>
            </div>
          </div>
        <!-- /.l-unit__videoPlayer image --></div>

        <div
          v-if="video.isShowBlock"
          class="l-unit__videoPlayer--action">
          <div
            class="o-action__videoThumbnail"
            :style="{
              '--button-color': corporateBrandColor.primary,
            }">
            <button class="o-action__videoThumbnail--item">
              <span class="o-action__videoThumbnail--label is-play">
                再生
              </span>
              <span
                v-if="video.duration"
                class="o-action__videoThumbnail--label is-time">
                {{ videoDuration }}
              </span>
            </button>
          <!-- /.o-action__videoThumbnail --></div>
        <!-- /.l-unit__videoPlayer action --></div>
      <!-- /.l-unit__videoPlayer --></div>
    </template>
  </SolutionContentsIntro>
</template>

<style lang="sass" scoped>
// layout
.l-unit__videoPlayer
  $root: &
  position: relative

  &[data-thumbnail="true"]
    #{$root}--image
      position: relative
      +transition

      &::before
        content: ""
        width: 100%
        height: 100%
        display: block
        position: absolute
        top: 0
        left: 0
        background-color: rgba($bg-white, 0)
        z-index: 1
        cursor: pointer
        +transition

      +mouseover
        &::before
          background-color: rgba($bg-white, 0.4)

    #{$root}--action
      position: absolute
      top: 50%
      left: 50%
      transform: translate(-50%, -50%)

// object
.o-label__noThumbnail
  $root: &
  $thumbnail-width: 960
  $thumbnail-height: 540
  position: relative
  padding-top: getRelativeValue($thumbnail-height, $thumbnail-width)
  background-color: rgba($bg-gray, 1)

  & &--item
    width: 100%
    position: absolute
    top: 50%
    left: 50%
    transform: translate(-50%, -50%)
    font:
      size: 5em
      weight: bold
    text-align: center

  +breakpoint
    #{$root}--item
      font-size: 3em

  +breakpoint($max-media: sp)
    #{$root}--item
      font-size: 2em

.o-action__videoThumbnail
  $root: &
  $button-size: 150
  $play-icon-size: 50px

  & &--item
    width: #{$button-size}px
    height: #{$button-size}px
    display: block
    position: relative
    overflow: hidden
    color: $font-white
    background-color: color-mix(in srgb, var(--button-color, $bg-orange) 80%, transparent)
    border: 5px solid $border-white
    border-radius: 50%

  & &--label
    &.is-play
      width: 100%
      height: 100%
      display: block
      position: relative
      overflow: hidden
      color: rgba($font-white, 0)
      white-space: nowrap

      &::before
        +iconTriangle($direction: right, $color: $bg-white, $width: $play-icon-size, $height: $play-icon-size)
        position: absolute
        top: 50%
        left: 50%
        transform: translate(-50%, -50%)
        margin-left: 5px  // adjust for centering position

    &.is-time
      width: 100%
      display: block
      position: absolute
      bottom: 0.3em
      left: 0
      text-align: center

  +breakpoint
    $rate: 0.8
    #{$root}--item
      width: #{$button-size * $rate}px
      height: #{$button-size * $rate}px

    #{$root}--label
      &.is-play
        &::before
          +iconTriangle($direction: right, $color: $bg-white, $width: $play-icon-size * $rate, $height: $play-icon-size * $rate)

      &.is-time
        font-size: $font-small

  +breakpoint($max-media: sp)
    $rate: 0.5
    #{$root}--item
      width: #{$button-size * $rate}px
      height: #{$button-size * $rate}px
      border-width: 3px

    #{$root}--label
      &.is-play
        &::before
          +iconTriangle($direction: right, $color: $bg-white, $width: $play-icon-size * $rate, $height: $play-icon-size * $rate)

      &.is-time
        font-size: $font-xx-small
</style>
